import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project } from '../../../types';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import BoardModal, {
  BMCancelButton, BMPrimaryButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT, BM_TEXTAREA,
} from '../../../components/BoardModal';
import {
  ReportTemplate, ReportSection, ReportRecipient, SECTION_LABEL, ReportType, REPORT_TYPE_LABEL,
  getProjectReportTemplates, createReportTemplate, updateReportTemplate, deleteReportTemplate,
  markGenerated, markSent, parseSections, parseRecipients, generateReportHtml, sendReportEmail,
} from '../../../services/reportTemplateService';
import {
  ExportJob, EXPORT_TYPE_LABEL, FORMAT_LABEL, STATUS_LABEL,
  getProjectExportJobs,
  exportTaskList, exportIssueRegister, exportRiskRegister, exportTimeReport, exportBudgetReport, exportFullProject,
} from '../../../services/exportService';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const ALL_SECTIONS: ReportSection[] = ['tasks', 'issues', 'risks', 'milestones', 'budget', 'team', 'notes'];

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export default function ReportsTab({ pca, projectId, project }: Props) {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ReportTemplate | 'new' | null>(null);
  const [preview, setPreview] = useState<{ template: ReportTemplate; html: string } | null>(null);
  const [sending, setSending] = useState<ReportTemplate | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [exportingType, setExportingType] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [t, j] = await Promise.all([
        getProjectReportTemplates(pca, projectId),
        getProjectExportJobs(pca, projectId),
      ]);
      setTemplates(t); setJobs(j);
    } finally { setLoading(false); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const onGenerate = async (tpl: ReportTemplate) => {
    setBusyId(tpl.dagrun_pm_reporttemplateid);
    try {
      const config = parseSections(tpl.dagrun_pm_sectionsjson);
      const html = await generateReportHtml(pca, project, config);
      const now = await markGenerated(pca, tpl.dagrun_pm_reporttemplateid);
      setTemplates(prev => prev.map(x => x.dagrun_pm_reporttemplateid === tpl.dagrun_pm_reporttemplateid ? { ...x, dagrun_pm_lastgeneratedon: now } : x));
      setPreview({ template: { ...tpl, dagrun_pm_lastgeneratedon: now }, html });
    } finally { setBusyId(null); }
  };

  const runExport = async (key: string, fn: () => Promise<void>) => {
    setExportingType(key); setExportError(null);
    try {
      await fn();
      await reload(); // refresh export history
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportingType(null);
    }
  };

  const timeOn = !!project.dagrun_pm_module_timetracking;
  const budgetOn = !!project.dagrun_pm_module_budget;

  const EXPORTS: { key: string; label: string; disabled?: boolean; run: () => Promise<void> }[] = [
    { key: 'tasks',  label: 'Task List',      run: () => exportTaskList(pca, project) },
    { key: 'issues', label: 'Issue Register', run: () => exportIssueRegister(pca, project) },
    { key: 'risks',  label: 'Risk Register',  run: () => exportRiskRegister(pca, project) },
    { key: 'time',   label: 'Time Report',    disabled: !timeOn,   run: () => exportTimeReport(pca, project) },
    { key: 'budget', label: 'Budget Report',  disabled: !budgetOn, run: () => exportBudgetReport(pca, project) },
    { key: 'full',   label: 'Full Project (JSON)', run: () => exportFullProject(pca, project) },
  ];

  return (
    <div style={{ padding: '24px 32px' }}>
      <h3 style={{ margin: 0, marginBottom: 20 }}>Reports</h3>

      {/* SECTION 1 — STATUS REPORTS */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span className="eyebrow">STATUS REPORTS</span>
          <button type="button" className="btn-primary" onClick={() => setEditing('new')}>+ New Report</button>
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
        ) : templates.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 0' }}>
            No report templates yet. Create one to generate and send status reports.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <Th>Report</Th><Th>Type</Th><Th>Last Generated</Th><Th>Last Sent</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => {
                const busy = busyId === t.dagrun_pm_reporttemplateid;
                return (
                  <tr key={t.dagrun_pm_reporttemplateid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                    <Td><span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>{t.dagrun_pm_name}</span></Td>
                    <Td><span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t.dagrun_pm_reporttype != null ? REPORT_TYPE_LABEL[t.dagrun_pm_reporttype] ?? '—' : '—'}</span></Td>
                    <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{fmtDate(t.dagrun_pm_lastgeneratedon)}</span></Td>
                    <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{fmtDate(t.dagrun_pm_lastsenton)}</span></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <LinkBtn onClick={() => onGenerate(t)} disabled={busy}>{busy ? 'Generating…' : 'Generate'}</LinkBtn>
                        <LinkBtn onClick={() => setSending(t)} disabled={!t.dagrun_pm_lastgeneratedon} title={t.dagrun_pm_lastgeneratedon ? '' : 'Generate first'}>Send</LinkBtn>
                        <LinkBtn onClick={() => setEditing(t)}>Edit</LinkBtn>
                        <LinkBtn danger onClick={() => deleteReportTemplate(pca, t.dagrun_pm_reporttemplateid).then(reload)}>Delete</LinkBtn>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* SECTION 2 — EXPORTS */}
      <div className="card" style={{ padding: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>EXPORT PROJECT DATA</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          {EXPORTS.map(ex => {
            const busy = exportingType === ex.key;
            return (
              <button key={ex.key} type="button"
                disabled={ex.disabled || busy}
                onClick={() => runExport(ex.key, ex.run)}
                title={ex.disabled ? 'Enable the related module to export this' : ''}
                style={{
                  background: ex.disabled ? 'var(--surface-2)' : '#fff',
                  color: ex.disabled ? 'var(--ink-4)' : '#0E7490',
                  border: `1px solid ${ex.disabled ? 'var(--border-light)' : '#0E7490'}`,
                  padding: '8px 14px', borderRadius: 6,
                  fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
                  cursor: ex.disabled || busy ? 'not-allowed' : 'pointer',
                }}>
                {busy ? 'Exporting…' : ex.label}
              </button>
            );
          })}
        </div>
        {exportError && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{exportError}</div>}

        <div className="eyebrow" style={{ marginTop: 20, marginBottom: 10 }}>EXPORT HISTORY</div>
        {jobs.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>No exports yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <Th>Export Type</Th><Th>Format</Th><Th>Generated</Th><Th>Expires</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.dagrun_pm_exportjobid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                  <Td><span style={{ fontSize: 13, color: 'var(--ink)' }}>{j.dagrun_pm_exporttype != null ? EXPORT_TYPE_LABEL[j.dagrun_pm_exporttype] ?? '—' : '—'}</span></Td>
                  <Td><span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{j.dagrun_pm_format != null ? FORMAT_LABEL[j.dagrun_pm_format] ?? '—' : '—'}</span></Td>
                  <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{fmtDate(j.createdon)}</span></Td>
                  <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{fmtDate(j.dagrun_pm_expireson)}</span></Td>
                  <Td><span style={{ fontSize: 12, color: '#16A34A', fontWeight: 600 }}>{j.dagrun_pm_status != null ? STATUS_LABEL[j.dagrun_pm_status] ?? '—' : '—'}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <TemplateEditor pca={pca} projectId={projectId}
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}

      {preview && (
        <BoardModal open title={`Preview — ${preview.template.dagrun_pm_name}`} width={820} onClose={() => setPreview(null)}
          footer={<BMCancelButton onClick={() => setPreview(null)}>Close</BMCancelButton>}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 6, padding: 16 }}
            dangerouslySetInnerHTML={{ __html: preview.html }} />
        </BoardModal>
      )}

      {sending && (
        <SendModal pca={pca} project={project} template={sending}
          onClose={() => setSending(null)}
          onSent={() => { setSending(null); reload(); }} />
      )}
    </div>
  );
}

// ── Template create/edit drawer ───────────────────────────────────────

function TemplateEditor({ pca, projectId, template, onClose, onSaved }: {
  pca: IPublicClientApplication; projectId: string; template: ReportTemplate | null;
  onClose: () => void; onSaved: () => void;
}) {
  const existing = template ? parseSections(template.dagrun_pm_sectionsjson) : { sections: ['tasks', 'issues', 'risks', 'milestones'] as ReportSection[] };
  const [name, setName] = useState(template?.dagrun_pm_name ?? '');
  const [reportType, setReportType] = useState<number>(template?.dagrun_pm_reporttype ?? ReportType.WeeklyStatus);
  const [sections, setSections] = useState<ReportSection[]>(existing.sections);
  const [notes, setNotes] = useState(existing.notes ?? '');
  const [recipients, setRecipients] = useState<ReportRecipient[]>(template ? parseRecipients(template.dagrun_pm_recipientlistjson) : []);
  const [picker, setPicker] = useState<Person | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSection = (s: ReportSection) =>
    setSections(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const addRecipient = (p: Person | null) => {
    if (!p) return;
    if (!recipients.some(r => r.id === p.aadObjectId)) {
      setRecipients(prev => [...prev, { id: p.aadObjectId, name: p.displayName, email: p.mail ?? null }]);
    }
    setPicker(null);
  };

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const payload = { name: name.trim(), reportType, sections, notes: notes.trim() || undefined, recipients };
      if (template) await updateReportTemplate(pca, template.dagrun_pm_reporttemplateid, payload);
      else await createReportTemplate(pca, { projectId, ...payload });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title={template ? 'Edit Report' : 'New Report'} width={520} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Saving…' : 'Save Report'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Report Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Report Type</label>
        <select value={reportType} onChange={e => setReportType(Number(e.target.value))} style={BM_INPUT}>
          {Object.entries(REPORT_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Sections to include</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
          {ALL_SECTIONS.map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={sections.includes(s)} onChange={() => toggleSection(s)} />
              {SECTION_LABEL[s]}
            </label>
          ))}
        </div>
      </div>
      {sections.includes('notes') && (
        <div style={{ marginBottom: 14 }}>
          <label style={BM_LABEL}>Custom Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} style={BM_TEXTAREA} placeholder="Notes to include in the report…" />
        </div>
      )}
      <div style={{ marginBottom: 6 }}>
        <label style={BM_LABEL}>Recipients</label>
        <PeoplePicker pca={pca} value={picker} onChange={addRecipient} placeholder="Add a recipient…" />
        {recipients.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {recipients.map(r => (
              <span key={r.id ?? r.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', borderRadius: 12, padding: '3px 10px', fontSize: 12, color: 'var(--ink-2)' }}>
                {r.name}{!r.email && <span style={{ color: '#D97706' }} title="No email — won't receive the report">⚠</span>}
                <button type="button" onClick={() => setRecipients(prev => prev.filter(x => x !== r))}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 14, lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// ── Send modal ────────────────────────────────────────────────────────

function SendModal({ pca, project, template, onClose, onSent }: {
  pca: IPublicClientApplication; project: Project; template: ReportTemplate;
  onClose: () => void; onSent: () => void;
}) {
  const [recipients, setRecipients] = useState<ReportRecipient[]>(parseRecipients(template.dagrun_pm_recipientlistjson));
  const [picker, setPicker] = useState<Person | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = (p: Person | null) => {
    if (!p) return;
    if (!recipients.some(r => r.id === p.aadObjectId)) {
      setRecipients(prev => [...prev, { id: p.aadObjectId, name: p.displayName, email: p.mail ?? null }]);
    }
    setPicker(null);
  };

  const send = async () => {
    if (sending) return;
    setSending(true); setError(null);
    try {
      const config = parseSections(template.dagrun_pm_sectionsjson);
      const html = await generateReportHtml(pca, project, config);
      await sendReportEmail(pca, recipients, `${project.dagrun_pm_name} — ${template.dagrun_pm_name}`, html);
      await markSent(pca, template.dagrun_pm_reporttemplateid);
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally { setSending(false); }
  };

  const sendable = recipients.some(r => r.email && r.email.includes('@'));

  return (
    <BoardModal open title={`Send — ${template.dagrun_pm_name}`} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={sending} />
        <BMPrimaryButton onClick={send} disabled={sending || !sendable}>{sending ? 'Sending…' : 'Send Report'}</BMPrimaryButton>
      </>}>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12 }}>
        The report is emailed via your Microsoft 365 account. Recipients without an email address are skipped.
      </div>
      <label style={BM_LABEL}>Recipients</label>
      <PeoplePicker pca={pca} value={picker} onChange={add} placeholder="Add a recipient…" />
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {recipients.map(r => (
          <span key={r.id ?? r.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', borderRadius: 12, padding: '3px 10px', fontSize: 12, color: 'var(--ink-2)' }}>
            {r.name}{!r.email && <span style={{ color: '#D97706' }} title="No email — skipped">⚠</span>}
            <button type="button" onClick={() => setRecipients(prev => prev.filter(x => x !== r))}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 14, lineHeight: 1 }}>×</button>
          </span>
        ))}
        {recipients.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>No recipients yet.</span>}
      </div>
      {!sendable && recipients.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#D97706' }}>None of the recipients have an email address on file.</div>
      )}
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// ── small helpers ─────────────────────────────────────────────────────

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
function LinkBtn({ children, onClick, disabled, danger, title }: {
  children: React.ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean; title?: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{
        background: 'transparent', border: 'none', padding: 0,
        color: disabled ? 'var(--ink-5)' : (danger ? '#DC2626' : '#0E7490'),
        fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}>{children}</button>
  );
}
